import _pick from 'lodash/pick';
import _get from 'lodash/get';
import {
  PerformanceLegend,
  PerformanceLegendItem,
  PerformanceMetric,
} from '../types';

// Updates a GeoJSON FeatureCollection by applying legend-based colors
// using provided performance metrics (sites or sectors).
const getMapData = (
  mapData: any,
  metrics: PerformanceMetric[],
  legend: PerformanceLegend,
) => {
  if (!mapData?.features?.length || !legend?.items?.length) return mapData;

  const newMapData = JSON.parse(JSON.stringify(mapData));

  const isAnomaly = String(legend.kind).toUpperCase() === 'ANOMALY';

  const itemsSorted: PerformanceLegendItem[] = [...legend.items].sort(
    (a, b) => (a.from ?? -Infinity) - (b.from ?? -Infinity),
  );

  const getLegendColorForValue = (value?: number) => {
    if (value === undefined || value === null || Number.isNaN(value)) {
      return 'green';
    }
    const found = itemsSorted.find((item) => {
      const from = item.from ?? -Infinity;
      const to = item.to === null || item.to === undefined ? Infinity : item.to;
      return value >= from && value <= to;
    });
    return found?.color;
  };

  // Build lookup maps for both site and sector metrics.
  // This allows coloring mixed FeatureCollections (sectors + sites),
  // e.g. when monitoring sectors with site overlay enabled.
  const metricBySectorId = new Map<string, PerformanceMetric>();
  const metricBySiteId = new Map<string, PerformanceMetric>();
  (metrics || []).forEach((m) => {
    const sectorKey = (m as any)?.id as string | undefined;
    const siteKey = (m as any)?.id as string | undefined;
    if (sectorKey) metricBySectorId.set(sectorKey, m);
    if (siteKey) metricBySiteId.set(siteKey, m);
  });

  newMapData.features = (newMapData.features || []).map((feature: any) => {
    const props = feature?.properties || {};
    const sourceId = props.sourceables?.[0]?.id as string | undefined;
    const sourceType = props.sourceables?.[0]?.type as string | undefined; // 'SITE' | 'SECTOR'
    // Choose metric map by feature type when available, otherwise fall back to legend entity
    let metric: PerformanceMetric | undefined;
    if (sourceId) {
      if (sourceType === 'SITE') {
        metric = metricBySiteId.get(sourceId) as PerformanceMetric | undefined;
        // fallback to sector metrics if site metric is missing and legend targets sectors
        if (!metric && legend.entity === 'SECTOR') {
          metric = metricBySectorId.get(sourceId) as
            PerformanceMetric | undefined;
        }
      } else if (sourceType === 'SECTOR') {
        metric = metricBySectorId.get(sourceId) as
          PerformanceMetric | undefined;
      } else {
        // Unknown type: use legend context to pick a map
        metric =
          (legend.entity === 'SECTOR'
            ? metricBySectorId.get(sourceId)
            : metricBySiteId.get(sourceId)) || undefined;
      }
    }

    const value: number | undefined = isAnomaly
      ? _get(metric, 'totalAnomalies') || 0
      : _get(metric, 'successPercentage') || 0;
    const legendColor = getLegendColorForValue(value);

    const newProps = {
      ..._pick(props, [
        'address',
        'addressDetails',
        'pointCount',
        'sourceables',
        'type',
      ]),
      color: legendColor,
      legendMatch: {
        entity: legend.entity,
        kind: legend.kind,
        value,
        id: sourceId,
        color: legendColor,
      },
    };

    return { ...feature, properties: newProps };
  });

  return newMapData;
};

export default getMapData;
